import type { FormPickerOption } from '@/components/molecules/FormPicker';
import type { FlatList } from 'react-native';
import { useEffect, useRef, type RefObject } from 'react';

/** Desktop mat-select row (minHeight 44). */
export const OPTION_PICKER_DROPDOWN_ROW_HEIGHT = 44;

/** Bottom sheet row incl. list gap (~48 + 8). */
export const OPTION_PICKER_SHEET_ROW_HEIGHT = 56;

export function findSelectedOptionIndex(
    options: ReadonlyArray<FormPickerOption>,
    selectedKey?: string | null,
): number {
    if (!selectedKey) {
        return -1;
    }
    return options.findIndex((opt) => (opt.key || opt.id) === selectedKey);
}

interface UseScrollOptionPickerToSelectedParams {
    listRef: RefObject<FlatList<FormPickerOption> | null>;
    visible: boolean;
    loading: boolean;
    options: ReadonlyArray<FormPickerOption>;
    selectedKey?: string | null;
    rowHeight: number;
    /** Matches FlatList `contentContainerStyle` vertical padding (top). */
    contentPaddingVertical?: number;
    /** When set, scroll offset is clamped so the row stays fully inside the list viewport. */
    listViewportHeight?: number;
}

function computeScrollOffsetForSelectedRow(params: {
    index: number;
    rowHeight: number;
    itemCount: number;
    contentPaddingVertical: number;
    listViewportHeight: number;
}): number {
    const { index, rowHeight, itemCount, contentPaddingVertical, listViewportHeight } = params;
    const contentHeight = contentPaddingVertical * 2 + itemCount * rowHeight;
    const maxOffset = Math.max(0, contentHeight - listViewportHeight);
    const itemTop = contentPaddingVertical + index * rowHeight;
    const centeredOffset = itemTop - (listViewportHeight - rowHeight) / 2;
    return Math.min(maxOffset, Math.max(0, centeredOffset));
}

/**
 * When the picker opens, scroll so the current selection is in view (mat-select behavior).
 */
export function useScrollOptionPickerToSelected({
    listRef,
    visible,
    loading,
    options,
    selectedKey,
    rowHeight,
    contentPaddingVertical = 0,
    listViewportHeight,
}: UseScrollOptionPickerToSelectedParams): {
    getItemLayout: (
        _data: ArrayLike<FormPickerOption> | null | undefined,
        index: number,
    ) => { length: number; offset: number; index: number };
    onScrollToIndexFailed: (info: { index: number }) => void;
} {
    const lastScrolledKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!visible || loading) {
            if (!visible) {
                lastScrolledKeyRef.current = null;
            }
            return;
        }

        const index = findSelectedOptionIndex(options, selectedKey);
        if (index < 0) {
            return;
        }

        const scrollKey = `${selectedKey}:${index}:${options.length}`;
        if (lastScrolledKeyRef.current === scrollKey) {
            return;
        }

        const scrollToSelected = (): void => {
            const list = listRef.current;
            if (!list) {
                return;
            }

            if (listViewportHeight != null && listViewportHeight > 0) {
                list.scrollToOffset({
                    offset: computeScrollOffsetForSelectedRow({
                        index,
                        rowHeight,
                        itemCount: options.length,
                        contentPaddingVertical,
                        listViewportHeight,
                    }),
                    animated: false,
                });
            } else {
                list.scrollToIndex({
                    index,
                    animated: false,
                    viewPosition: 0,
                    viewOffset: contentPaddingVertical,
                });
            }
            lastScrolledKeyRef.current = scrollKey;
        };

        requestAnimationFrame(() => {
            requestAnimationFrame(scrollToSelected);
        });
    }, [
        visible,
        loading,
        options,
        selectedKey,
        rowHeight,
        contentPaddingVertical,
        listViewportHeight,
        listRef,
    ]);

    return {
        getItemLayout: (_data, index) => ({
            length: rowHeight,
            offset: contentPaddingVertical + rowHeight * index,
            index,
        }),
        onScrollToIndexFailed: (info) => {
            const offset =
                listViewportHeight != null && listViewportHeight > 0
                    ? computeScrollOffsetForSelectedRow({
                          index: info.index,
                          rowHeight,
                          itemCount: options.length,
                          contentPaddingVertical,
                          listViewportHeight,
                      })
                    : Math.max(0, contentPaddingVertical + info.index * rowHeight);
            listRef.current?.scrollToOffset({
                offset,
                animated: false,
            });
        },
    };
}
